import React from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Loader2, Trash2, KeyRound, X, UserPlus } from 'lucide-react';
import {
  useAdminUserList,
  useCreateAdminUser,
  useUpdateAdminUser,
  useDeleteAdminUser,
  useResetAdminPassword,
} from '../hooks';
import { useAuth } from '../contexts/AuthContext';
import { AdminUser } from '../api/adminUsers';
import { getErrorDetail } from '../utils/error';
import { isReservedUsername } from '../api/reservedUsernames';

const roleBadge: Record<string, { bg: string; text: string }> = {
  super_admin: { bg: 'bg-purple-100', text: 'text-purple-700' },
  admin: { bg: 'bg-blue-100', text: 'text-blue-700' },
  viewer: { bg: 'bg-slate-100', text: 'text-slate-600' },
};

const AdminAccounts: React.FC = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { data, isLoading } = useAdminUserList();
  const updateUser = useUpdateAdminUser();
  const deleteUser = useDeleteAdminUser();
  const resetPassword = useResetAdminPassword();
  const createUser = useCreateAdminUser();
  const [deletingId, setDeletingId] = React.useState<string | null>(null);

  // Create user modal state
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [createEmail, setCreateEmail] = React.useState('');
  const [createPassword, setCreatePassword] = React.useState('');
  const [createName, setCreateName] = React.useState('');
  const [createRole, setCreateRole] = React.useState('viewer');
  const [createError, setCreateError] = React.useState('');
  const [isCreating, setIsCreating] = React.useState(false);

  // Reset password modal state
  const [resetTarget, setResetTarget] = React.useState<AdminUser | null>(null);
  const [resetNewPassword, setResetNewPassword] = React.useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = React.useState('');
  const [resetError, setResetError] = React.useState('');
  const [isResetting, setIsResetting] = React.useState(false);

  const users = data?.users || [];

  const handleRoleChange = async (adminUser: AdminUser, newRole: string) => {
    try {
      await updateUser.mutateAsync({
        userId: adminUser.id,
        data: { role: newRole },
      });
    } catch (error) {
      console.error('Failed to update role:', error);
    }
  };

  const isDisabled = (adminUser: AdminUser): boolean => {
    if (!currentUser) return true;
    // Cannot change own role
    if (adminUser.id === currentUser.id) return true;
    // super_admin role is immutable — nobody can change it
    if (adminUser.role === 'super_admin') return true;
    return false;
  };

  const getAvailableRoles = (): string[] => {
    return ['viewer', 'admin'];
  };

  const canDelete = (adminUser: AdminUser): boolean => {
    if (!currentUser) return false;
    if (currentUser.role === 'viewer') return false;
    if (adminUser.id === currentUser.id) return false;
    if (adminUser.role === 'super_admin' && currentUser.role !== 'super_admin') return false;
    return true;
  };

  const canResetPassword = (adminUser: AdminUser): boolean => {
    if (!currentUser) return false;
    if (currentUser.role === 'viewer') return false;
    if (adminUser.id === currentUser.id) return false;
    if (adminUser.role === 'super_admin' && currentUser.role !== 'super_admin') return false;
    return true;
  };

  const handleDelete = async (adminUser: AdminUser) => {
    if (!window.confirm(t('adminAccounts.deleteConfirm', { name: adminUser.name || adminUser.email }))) {
      return;
    }
    setDeletingId(adminUser.id);
    try {
      await deleteUser.mutateAsync(adminUser.id);
    } catch (error) {
      console.error('Failed to delete user:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const openCreateModal = () => {
    setCreateEmail('');
    setCreatePassword('');
    setCreateName('');
    setCreateRole('viewer');
    setCreateError('');
    setShowCreateModal(true);
  };

  const handleCreateUser = async () => {
    setCreateError('');

    if (!createEmail.trim()) {
      setCreateError(t('adminAccounts.emailRequired'));
      return;
    }
    if (isReservedUsername(createEmail.trim())) {
      setCreateError('사용할 수 없는 이메일 주소입니다.');
      return;
    }
    if (createPassword.length < 8) {
      setCreateError(t('password.passwordMinLength'));
      return;
    }

    setIsCreating(true);
    try {
      await createUser.mutateAsync({
        email: createEmail.trim(),
        password: createPassword,
        name: createName.trim() || undefined,
        role: createRole,
      });
      setShowCreateModal(false);
      alert(t('adminAccounts.createSuccess'));
    } catch (error: unknown) {
      setCreateError(getErrorDetail(error, t('common.error')));
    } finally {
      setIsCreating(false);
    }
  };

  const openResetModal = (adminUser: AdminUser) => {
    setResetTarget(adminUser);
    setResetNewPassword('');
    setResetConfirmPassword('');
    setResetError('');
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    setResetError('');

    if (resetNewPassword.length < 8) {
      setResetError(t('password.passwordMinLength'));
      return;
    }
    if (resetNewPassword !== resetConfirmPassword) {
      setResetError(t('password.passwordMismatch'));
      return;
    }

    setIsResetting(true);
    try {
      await resetPassword.mutateAsync({
        userId: resetTarget.id,
        newPassword: resetNewPassword,
      });
      setResetTarget(null);
      alert(t('password.resetSuccess'));
    } catch (error: unknown) {
      setResetError(getErrorDetail(error, t('common.error')));
    } finally {
      setIsResetting(false);
    }
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">{t('adminAccounts.title')}</h2>
          <p className="text-sm text-slate-500 mt-1">{t('adminAccounts.subtitle')}</p>
        </div>
        {currentUser?.role !== 'viewer' && (
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <UserPlus size={16} />
            {t('adminAccounts.createUser')}
          </button>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 size={32} className="animate-spin mx-auto mb-2 text-blue-500" />
            <p className="text-slate-500">{t('common.loading')}</p>
          </div>
        </div>
      ) : users.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <ShieldCheck size={48} className="mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">{t('adminAccounts.noUsers')}</h3>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                  {t('adminAccounts.email')}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                  {t('common.name')}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                  {t('adminAccounts.role')}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                  {t('common.status')}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                  {t('adminAccounts.lastLogin')}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                  {t('common.createdAt')}
                </th>
                {currentUser?.role !== 'viewer' && (
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                    {t('adminAccounts.actions')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {users.map((adminUser) => {
                const badge = roleBadge[adminUser.role] || roleBadge.viewer;
                const disabled = isDisabled(adminUser);
                return (
                  <tr key={adminUser.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <span className="font-medium text-slate-800">{adminUser.email}</span>
                      {adminUser.id === currentUser?.id && (
                        <span className="ml-2 text-xs text-blue-500">({t('adminAccounts.you')})</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{adminUser.name || '-'}</td>
                    <td className="px-4 py-3">
                      {disabled ? (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}
                        >
                          {adminUser.role}
                        </span>
                      ) : (
                        <select
                          value={adminUser.role}
                          onChange={(e) => handleRoleChange(adminUser, e.target.value)}
                          disabled={updateUser.isPending}
                          className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-300 ${badge.bg} ${badge.text}`}
                        >
                          {getAvailableRoles().map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {adminUser.is_active ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          {t('tiers.active')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                          {t('tiers.inactive')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">{formatDate(adminUser.last_login_at)}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">{formatDate(adminUser.created_at)}</td>
                    {currentUser?.role !== 'viewer' && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {canResetPassword(adminUser) && (
                            <button
                              onClick={() => openResetModal(adminUser)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-amber-500 hover:bg-amber-50 transition-colors"
                              title={t('adminAccounts.resetPassword')}
                            >
                              <KeyRound size={16} />
                            </button>
                          )}
                          {canDelete(adminUser) ? (
                            <button
                              onClick={() => handleDelete(adminUser)}
                              disabled={deletingId === adminUser.id}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                              title={t('adminAccounts.deleteUser')}
                            >
                              {deletingId === adminUser.id ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <Trash2 size={16} />
                              )}
                            </button>
                          ) : !canResetPassword(adminUser) ? (
                            <span className="text-slate-300">-</span>
                          ) : null}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-800">{t('adminAccounts.createUserTitle')}</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('adminAccounts.email')} *</label>
                <input
                  type="email"
                  value={createEmail}
                  onChange={(e) => setCreateEmail(e.target.value)}
                  placeholder={t('adminAccounts.emailPlaceholder')}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('password.newPassword')} *</label>
                <input
                  type="password"
                  value={createPassword}
                  onChange={(e) => setCreatePassword(e.target.value)}
                  placeholder={t('adminAccounts.passwordPlaceholder')}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('common.name')}</label>
                <input
                  type="text"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder={t('adminAccounts.namePlaceholder')}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('adminAccounts.role')}</label>
                <select
                  value={createRole}
                  onChange={(e) => setCreateRole(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >
                  {getAvailableRoles().map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>

              {createError && <p className="text-sm text-red-500">{createError}</p>}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateUser}
                disabled={isCreating || !createEmail || !createPassword}
                className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? t('adminAccounts.creating') : t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-800">{t('password.resetPassword')}</h3>
              <button
                onClick={() => setResetTarget(null)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-sm text-slate-600 mb-4">
              {t('adminAccounts.resetConfirmMessage', { name: resetTarget.name || resetTarget.email })}
            </p>

            <div className="space-y-4">
              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('password.newPassword')}</label>
                <input
                  type="password"
                  value={resetNewPassword}
                  onChange={(e) => setResetNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('password.confirmPassword')}</label>
                <input
                  type="password"
                  value={resetConfirmPassword}
                  onChange={(e) => setResetConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              {/* Error */}
              {resetError && <p className="text-sm text-red-500">{resetError}</p>}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setResetTarget(null)}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleResetPassword}
                disabled={isResetting || !resetNewPassword || !resetConfirmPassword}
                className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResetting ? t('common.loading') : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAccounts;
